import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  configuredAvailability,
  pickableForms,
  scratchSelection,
} from "@/components/app/admission-packet/forms/form-selection";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RecordCard } from "@/components/app/lists/record-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import {
  PacketTemplateEditor,
  type TemplateDraft,
} from "@/routes/_authenticated/_administration/settings/-components/forms/packet-template-editor";
import {
  PacketTemplateList,
  type TemplateRow,
} from "@/routes/_authenticated/_administration/settings/-components/forms/packet-template-list";
import { useAdmissionFacility } from "@/routes/_authenticated/_administration/settings/-components/forms/use-admission-facility";

export const Route = createFileRoute("/_authenticated/_administration/settings/move-in/templates")({
  component: AdmissionPacketTemplateSettings,
});

const PACKET_TYPES = [
  { value: "resident", label: "Resident admission" },
  { value: "therapy", label: "Therapy" },
] as const;

type TemplatePacketType = (typeof PACKET_TYPES)[number]["value"];

/** Templates exist because an admission packet has twenty-nine forms and each stay type (respite,
 * readmission, memory care) needs a different subset. */
function AdmissionPacketTemplateSettings() {
  const facility = useAdmissionFacility();
  // Building admins hold this at their building, never company-wide, so ask about this one.
  const canManage = useCan("settings:manage", facility.facilityId);
  const [packetType, setPacketType] = useState<TemplatePacketType>("resident");
  const [editing, setEditing] = useState<TemplateDraft | null>(null);
  const [deleting, setDeleting] = useState<TemplateRow | null>(null);

  const settings = useQuery({
    ...orpc.care.admissions.exhibitSettings.get.queryOptions({
      input: { facilityId: facility.facilityId },
    }),
    enabled: facility.facilityId !== "",
  });
  const templates = useQuery({
    ...orpc.care.admissions.templates.list.queryOptions({
      input: { facilityId: facility.facilityId, packetType },
    }),
    enabled: facility.facilityId !== "",
  });

  const configured = useMemo(
    () => configuredAvailability(settings.data?.availability),
    [settings.data],
  );
  const forms = useMemo(() => pickableForms(packetType, configured), [packetType, configured]);

  return (
    <>
      <div className="flex flex-wrap items-start gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="packet-template-kind">Packet kind</Label>
          <Select
            value={packetType}
            onValueChange={(value) => setPacketType(value as TemplatePacketType)}
          >
            <SelectTrigger id="packet-template-kind" className="w-full sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PACKET_TYPES.map((entry) => (
                <SelectItem key={entry.value} value={entry.value}>
                  {entry.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <RecordCard
        title="Templates"
        count={templates.data?.length}
        canWrite={canManage}
        addLabel="Add template"
        onAdd={() =>
          setEditing({
            name: "",
            description: null,
            // A new template starts where a packet from scratch starts, so it
            // is edited down to the exception rather than built up from zero.
            sectionKeys: scratchSelection(packetType, configured),
            isDefault: false,
          })
        }
      >
        {templates.isError ? (
          <ReadFailed
            message="The move-in templates could not be loaded."
            error={templates.error}
            retrying={templates.isFetching}
            onRetry={() => void templates.refetch()}
          />
        ) : (
          <>
            {settings.isError ? (
              <ReadFailed
                className="mb-3"
                message="This building’s form choices could not be loaded, so a new template starts from the standard set."
                error={settings.error}
                retrying={settings.isFetching}
                onRetry={() => void settings.refetch()}
              />
            ) : null}
            <PacketTemplateList
              templates={templates.data ?? []}
              isLoading={facility.facilityId === "" || templates.isPending}
              canManage={canManage}
              deleting={deleting}
              onEdit={(template) =>
                setEditing({
                  id: template.id,
                  name: template.name,
                  description: template.description,
                  sectionKeys: template.sectionKeys,
                  isDefault: template.isDefault,
                })
              }
              onDeleteChange={setDeleting}
            />
          </>
        )}
      </RecordCard>

      {editing ? (
        <PacketTemplateEditor
          // Remounted per template, so the dialog's fields start from the one
          // being edited rather than from whichever was opened first.
          key={editing.id ?? "new"}
          open
          onOpenChange={(value) => (value ? null : setEditing(null))}
          facilityId={facility.facilityId}
          packetType={packetType}
          forms={forms}
          draft={editing}
        />
      ) : null}
    </>
  );
}
